import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames/bind';
import styles from './Progress.module.css';

import { useGetStorageQuery } from '../../services/main';
import { useInterval } from '../../hooks/useInterval';

import { MOVE_TO_STORAGE, PROGRESS_DELAY } from '../../constants';
import { formatBytes } from '../../utils/util';

import { useDispatch } from 'react-redux';
import { selectStorage, storageChanged } from '../../features/storageSlice';
import { useAppSelector } from '../../hooks/useRedux';

interface Progress {
  isOpenHamburgerMenu: boolean;
  closeMenu: () => void;
}

export default function Progress({ isOpenHamburgerMenu, closeMenu }: Progress) {
  const cx = classNames.bind(styles);
  const dispatch = useDispatch();
  const { data, isLoading } = useGetStorageQuery();
  const navigate = useNavigate();
  const storage = useAppSelector(selectStorage);

  const [progress, setProgress] = useState(0);
  const [usedPercent, setUsedPercent] = useState(0);
  const [totalStorage, setTotalStorage] = useState('');
  const [isNotEnoughStorage, setIsNotEnoughStorage] = useState(false);

  const movePage = () => {
    navigate('settings/storage');
    closeMenu();
  };

  useInterval(
    () => setProgress((prev) => prev + 1),
    progress < usedPercent ? PROGRESS_DELAY : null
  );

  useEffect(() => {
    if (storage) {
      const { used, limit } = storage;
      const totalStorageStr = formatBytes(limit, 3);
      const percent = Math.round((used / limit) * 100);

      setProgress(0);
      setUsedPercent(percent >= 100 ? 100 : percent);
      setTotalStorage(totalStorageStr);
      setIsNotEnoughStorage(usedPercent > 90);
    }
  }, [storage, usedPercent]);

  useEffect(() => {
    if (data) {
      dispatch(storageChanged(data));
    }
  }, [data]);

  if (isLoading) return null;

  return (
    <div
      title={MOVE_TO_STORAGE}
      className={cx('drive_info', { 'layer--open': isOpenHamburgerMenu })}
      onClick={movePage}
    >
      <div className={cx('progress_bar')}>
        <div className={cx('progress', 'rail')}></div>
        <div
          className={cx('progress', 'current', { point: isNotEnoughStorage })}
          style={{ width: `${progress}%` }}
        ></div>
      </div>

      <span>
        <span className={cx({ point: isNotEnoughStorage })}>
          {usedPercent}%
        </span>
        <span> of {totalStorage}</span>
      </span>
    </div>
  );
}
